/* eslint-disable max-lines */
import {
  PaymentMethodResult,
  PaymentRequestPaymentMethodEvent,
  Stripe,
  StripeCardNumberElement,
  StripeError,
} from '@stripe/stripe-js'
import { paymentApi, userApi } from 'api'
import i18n from 'i18next'

import {
  setErrorAction,
  startFetching,
  stopFetching,
} from 'root-redux/actions/common'
import {
  sendPlanAdditionsAction,
  sendUserConfigAction,
} from 'root-redux/actions/user'
import {
  selectCurrentVariantCohort,
  selectScreenName,
} from 'root-redux/selects/common'
import {
  selectEmail,
  selectIsUpgradePassed,
  selectPixPurchaseData,
  selectProcessorName,
  selectUUID,
  selectUpiPurchaseData,
  selectUserPaymentMethod,
  selectUserPaymentSystem,
} from 'root-redux/selects/user'

import { IAction, IAppState, TAppDispatchThunk } from 'models/store.model'
import { ISubscription } from 'models/subscriptions.model'

import {
  Events,
  ScreenName,
  Source,
  eventLogger,
} from 'services/eventLogger.service'
import { googleAnalyticsLogger } from 'services/googleAnalytics.service'

import { IBoletoData } from 'modules/payment/components/BoletoPaymentForm/constants'
import {
  DEFAULT_CARDHOLDER_NAME,
  PaymentMethod,
  PaymentSystem,
  StripeDeclineReason,
  StripeSoftDeclineReason,
} from 'modules/payment/constants'
import { getAxonItems } from 'modules/payment/helpers/getAxonItem'
import { getPurchaseFailedEventParams } from 'modules/payment/helpers/getPurchaseFailedEventParams'
import { getPurchaseStartedEventParams } from 'modules/payment/helpers/getPurchaseStartedEventParams'
import { getPurchaseSuccessEventParams } from 'modules/payment/helpers/getPurchaseSuccessEventParams'
import { getRedirectUrl } from 'modules/payment/helpers/getRedirectUrl'

import {
  CENTS_IN_DOLLAR,
  PlanAddition,
  SubscriptionListType,
} from 'root-constants/common'

import { checkIsRetryAllowed } from '../../helpers/checkIsRetryAllowed'
import { logFailedPayment } from '../../helpers/logFailedPayment'
import { logSuccessfulPayment } from '../../helpers/logSuccessfulPayment'
import {
  selectCpf,
  selectCreatedSubscriptionId,
  selectCurrency,
  selectPaymentClientSecret,
  selectPaymentMethod,
  selectPlanId,
  selectProductId,
  selectSubscription,
  selectSubscriptionFullPrice,
  selectSubscriptionLookupKey,
  selectSubscriptionPeriodName,
  selectSubscriptionPeriodQuantity,
  selectSubscriptionTrialLookupKey,
  selectSubscriptionTrialPeriodDays,
  selectSubscriptionTrialPeriodPrice,
  selectUpgradeAmountToPay,
} from '../selects'

const MODULE_NAME = 'PAYMENT'

export const SET_SUBSCRIPTION = `${MODULE_NAME}/SET_SUBSCRIPTION`
export const PURCHASE = `${MODULE_NAME}/PURCHASE`
export const CHECK_3D_SECURE = `${MODULE_NAME}/CHECK_3D_SECURE`
export const SET_3D_SECURE_IFRAME_URL = `${MODULE_NAME}/SET_3D_SECURE_IFRAME_URL`
export const RESET_3D_SECURE_IFRAME_URL = `${MODULE_NAME}/RESET_3D_SECURE_IFRAME_URL`
export const SET_PAYMENT_CLIENT_SECRET = `${MODULE_NAME}/SET_PAYMENT_CLIENT_SECRET`
export const SET_TRIAL_PERIOD_DAYS = `${MODULE_NAME}/SET_TRIAL_PERIOD_DAYS`
export const SET_SUBSCRIPTION_ID = `${MODULE_NAME}/SET_SUBSCRIPTION_ID`
export const SET_CPF = `${MODULE_NAME}/SET_CPF`
export const SET_IS_BOLETO_VOUCHER_CLOSED = `${MODULE_NAME}/SET_IS_BOLETO_VOUCHER_CLOSED`
export const SET_IS_PIX_VOUCHER_CLOSED = `${MODULE_NAME}/SET_IS_PIX_VOUCHER_CLOSED`
export const SET_WAS_UPI_PAYMENT_INITIATED = `${MODULE_NAME}/SET_WAS_UPI_PAYMENT_INITIATED`
export const SET_IS_PAYMENT_FLOWS_SHOWN = `${MODULE_NAME}/SET_IS_PAYMENT_FLOWS_SHOWN`
export const SET_PLAN_ADDITIONS = `${MODULE_NAME}/SET_PLAN_ADDITIONS`
export const SET_PAYMENT_METHOD = `${MODULE_NAME}/SET_PAYMENT_METHOD`
export const SET_PAYMENT_REQUEST_BUTTON_TYPE = `${MODULE_NAME}/SET_PAYMENT_REQUEST_BUTTON_TYPE`
export const MAKE_UPSELL = `${MODULE_NAME}/MAKE_UPSELL`
export const SET_IS_PAYMENT_COMPLETED = `${MODULE_NAME}/SET_IS_PAYMENT_COMPLETED`
export const SET_IS_NOTIFICATION_SHOWN = `${MODULE_NAME}/SET_IS_NOTIFICATION_SHOWN`

const getErrorActionPayload = ({ type, message }: StripeError): string =>
  message || type

const set3DSecureIframeUrlAction = (payload: string): IAction<string> => ({
  type: SET_3D_SECURE_IFRAME_URL,
  payload,
})

const reset3DSecureIframeUrlAction = (): IAction<never> => ({
  type: RESET_3D_SECURE_IFRAME_URL,
})

const setPaymentClientSecretAction = (payload: string): IAction<string> => ({
  type: SET_PAYMENT_CLIENT_SECRET,
  payload,
})

const setSubscriptionIdAction = (payload: string): IAction<string> => ({
  type: SET_SUBSCRIPTION_ID,
  payload,
})

export const setCpfAction = (payload: string): IAction<string> => ({
  type: SET_CPF,
  payload,
})
export const setIsBoletoVoucherClosed = (
  payload: boolean,
): IAction<boolean> => ({
  type: SET_IS_BOLETO_VOUCHER_CLOSED,
  payload,
})

export const setIsPixVoucherClosed = (payload: boolean): IAction<boolean> => ({
  type: SET_IS_PIX_VOUCHER_CLOSED,
  payload,
})

export const setWasUpiPaymentInitiated = (
  payload: boolean,
): IAction<boolean> => ({
  type: SET_WAS_UPI_PAYMENT_INITIATED,
  payload,
})

export const setIsPaymentFlowsShownAction = (
  payload: boolean,
): IAction<boolean> => ({
  type: SET_IS_PAYMENT_FLOWS_SHOWN,
  payload,
})

export const setSelectedSubscriptionAction = (
  payload: ISubscription,
): IAction<ISubscription> => ({
  type: SET_SUBSCRIPTION,
  payload,
})

export const setPlanAdditionsAction = (
  payload: PlanAddition[],
): IAction<PlanAddition[]> => ({
  type: SET_PLAN_ADDITIONS,
  payload,
})

export const setPaymentMethodAction = (
  payload: PaymentMethod,
): IAction<PaymentMethod> => ({
  type: SET_PAYMENT_METHOD,
  payload,
})

export const setPaymentRequestButtonTypeAction = (
  payload: PaymentMethod,
): IAction<PaymentMethod> => ({
  type: SET_PAYMENT_REQUEST_BUTTON_TYPE,
  payload,
})

export const setIsPaymentCompletedAction = (
  payload: boolean,
): IAction<boolean> => ({
  type: SET_IS_PAYMENT_COMPLETED,
  payload,
})

export const setIsNotificationShown = (payload: boolean): IAction<boolean> => ({
  type: SET_IS_NOTIFICATION_SHOWN,
  payload,
})

export const purchaseAction =
  ({
    stripe,
    paymentPageId,
    card,
    name,
    createPaymentResFromDigitalWallet,
  }: {
    stripe: Stripe
    paymentPageId: string
    card?: StripeCardNumberElement
    name?: string
    createPaymentResFromDigitalWallet?: PaymentRequestPaymentMethodEvent
  }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const planId = selectPlanId(state)
    const priceId = selectSubscriptionLookupKey(state)
    const trialPriceId = selectSubscriptionTrialLookupKey(state)
    const trialPrice = selectSubscriptionTrialPeriodPrice(state)
    const uuid = selectUUID(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)
    const currency = selectCurrency(state)
    const cohort = selectCurrentVariantCohort(state)
    const selectedSubscription = selectSubscription(state)
    const email = selectEmail(state)

    if (!priceId || !currentPrice) {
      console.error('Error: no subscriptions plan selected')
      return
    }

    const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    dispatch(startFetching(PURCHASE))

    const paymentMethod =
      createPaymentResFromDigitalWallet?.paymentMethod?.card?.wallet?.type

    const isPaymentMethodFromDigitalWallet =
      paymentMethod === PaymentMethod.APPLE_PAY ||
      paymentMethod === PaymentMethod.GOOGLE_PAY

    googleAnalyticsLogger.logAddingToCart({
      subscription: selectedSubscription as ISubscription,
      email,
      uuid,
    })
    window.fbq('track', 'AddToCart', {}, { eventID: uuid })
    window.ttq.track('AddToCart')
    window.snaptr &&
      window.snaptr('track', 'ADD_CART', {
        user_email: commonPurchaseStartedParams.isPersonalDataAllowed
          ? email
          : '',
      })
    window.axon &&
      window.axon('track', 'begin_checkout', {
        value: trialPrice || currentPrice,
        currency: currency.toUpperCase(),
        items: getAxonItems([selectedSubscription as ISubscription]),
      })

    eventLogger.logPurchaseStarted({
      ...commonPurchaseStartedParams,
      paymentMethod,
      paymentSystem: PaymentSystem.STRIPE,
      eventName: isPaymentMethodFromDigitalWallet
        ? Events.PURCHASE_STARTED_POPUP
        : Events.PURCHASE_STARTED,
    })

    try {
      const createPaymentResponse =
        card && !createPaymentResFromDigitalWallet
          ? await stripe.createPaymentMethod({
              type: 'card',
              card,
              billing_details: { name: name || DEFAULT_CARDHOLDER_NAME },
            })
          : (createPaymentResFromDigitalWallet as PaymentMethodResult)

      if (
        !createPaymentResponse?.paymentMethod &&
        createPaymentResponse?.error
      ) {
        const {
          error: { type, message },
        } = createPaymentResponse

        eventLogger.logPurchaseFailed({
          ...commonPurchaseFailedParams,
          error: createPaymentResponse.error,
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })

        dispatch(setErrorAction(message || type))
        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      const isFreeTrial = !!trialPeriodDays && !trialPriceId
      const createSubscriptionResponse = await paymentApi.createSubscription({
        cohort,
        uuid,
        planId,
        trialPeriodDays,
        paymentMethodId: createPaymentResponse.paymentMethod.id,
      })

      if (
        !createSubscriptionResponse.success ||
        !createSubscriptionResponse.data
      ) {
        // It's a hack for fixing behavior after changing response
        // from WS BE after getting payment_method
        if (
          createSubscriptionResponse.data?.error?.startsWith(
            'creating new customer',
          )
        ) {
          let error = { type: createSubscriptionResponse.data?.error }

          try {
            error = JSON.parse(
              createSubscriptionResponse.data.error.split(
                'creating new customer: ',
              )[1],
            )
          } finally {
            logFailedPayment({
              ...commonPurchaseFailedParams,
              paymentResponse: error,
              paymentMethod,
              paymentSystem: PaymentSystem.STRIPE,
            })

            dispatch(
              setErrorAction({ type: createSubscriptionResponse.data?.error }),
            )
            dispatch(stopFetching(PURCHASE))
            createPaymentResFromDigitalWallet?.complete('fail')
          }
          return
        }

        if (isFreeTrial) {
          logFailedPayment({
            ...commonPurchaseFailedParams,
            paymentResponse: { type: createSubscriptionResponse.data?.error },
            paymentMethod,
            paymentSystem: PaymentSystem.STRIPE,
          })

          dispatch(
            setErrorAction({ type: createSubscriptionResponse.data?.error }),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }
        switch (createSubscriptionResponse.status) {
          case 409:
            dispatch(setErrorAction(i18n.t('payment.haveSubscription')))
            break
          default:
            dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
        }

        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: { type: createSubscriptionResponse.data?.error },
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })

        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      if (isFreeTrial) {
        const predictedLtvResponse = await userApi.getUserPredictedLtv(uuid)

        logSuccessfulPayment({
          ...commonPurchaseSuccessParams,
          predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
          predictedLtvCurrency: predictedLtvResponse?.data?.currency,
          productId: createSubscriptionResponse.data.purchase.product_id,
          trialPeriodDays:
            createSubscriptionResponse.data.purchase.trial_period_days,
          subscriptionId:
            createSubscriptionResponse.data.purchase.subscription_id,
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
          isUpsell: false,
          purchaseType: SubscriptionListType.PURCHASE,
        })

        dispatch(
          sendUserConfigAction({
            payment_currency: currency,
            payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
            subscription_price: currentPrice,
            subscription_duration: `${periodQuantity}${periodName}`,
            price_id: priceId,
            trial_price: trialPrice,
            trial_period: trialPeriodDays,
          }),
        )
        dispatch(sendPlanAdditionsAction())
        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('success')
        return
      }

      dispatch(
        setPaymentClientSecretAction(
          createSubscriptionResponse.data.purchase.client_secret,
        ),
      )
      dispatch(
        setSubscriptionIdAction(
          createSubscriptionResponse.data.purchase.subscription_id,
        ),
      )

      dispatch(setIsPaymentCompletedAction(true))

      const cardPaymentResponseFirst = await stripe.confirmCardPayment(
        createSubscriptionResponse.data.purchase.client_secret,
        {
          payment_method: createPaymentResponse.paymentMethod.id,
          save_payment_method: true,
          return_url: getRedirectUrl(paymentPageId),
        },
      )

      if (
        !cardPaymentResponseFirst?.paymentIntent &&
        cardPaymentResponseFirst?.error
      ) {
        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: cardPaymentResponseFirst.error,
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })
        if (!checkIsRetryAllowed(cardPaymentResponseFirst)) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseFirst.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        const retryResponseFirst = await paymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseFirst.error as StripeError,
        })

        if (!retryResponseFirst.data.should_retry) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseFirst.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        dispatch(
          setPaymentClientSecretAction(
            createSubscriptionResponse.data.purchase.client_secret,
          ),
        )
        dispatch(
          setSubscriptionIdAction(
            createSubscriptionResponse.data.purchase.subscription_id,
          ),
        )

        const cardPaymentResponseSecond = await stripe.confirmCardPayment(
          retryResponseFirst.data.subscription.client_secret,
          {
            payment_method: createPaymentResponse.paymentMethod.id,
            save_payment_method: true,
            return_url: getRedirectUrl(paymentPageId),
          },
        )

        if (
          cardPaymentResponseSecond?.paymentIntent &&
          !cardPaymentResponseSecond?.error
        ) {
          const predictedLtvResponse = await userApi.getUserPredictedLtv(uuid)

          logSuccessfulPayment({
            ...commonPurchaseSuccessParams,
            predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
            predictedLtvCurrency: predictedLtvResponse?.data?.currency,
            productId: createSubscriptionResponse.data.purchase.product_id,
            trialPrice:
              cardPaymentResponseSecond.paymentIntent.amount / CENTS_IN_DOLLAR,
            trialPeriodDays:
              retryResponseFirst.data.subscription.trial_period_days,
            subscriptionId:
              retryResponseFirst.data.subscription.subscription_id,
            discountApplied:
              retryResponseFirst.data.subscription.discount_applied,
            paymentMethod,
            paymentSystem: PaymentSystem.STRIPE,
            isUpsell: false,
            purchaseType: SubscriptionListType.PURCHASE,
          })

          dispatch(
            sendUserConfigAction({
              payment_currency: currency,
              payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
              discount_applied:
                !!retryResponseFirst.data.subscription.discount_applied,
              subscription_price: currentPrice,
              subscription_duration: `${periodQuantity}${periodName}`,
              price_id: priceId,
              trial_price: trialPrice,
              trial_period: trialPeriodDays,
            }),
          )
          dispatch(sendPlanAdditionsAction())
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('success')
          return
        }

        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: cardPaymentResponseSecond.error,
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })

        if (!checkIsRetryAllowed(cardPaymentResponseSecond)) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseSecond.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        const retryResponseSecond = await paymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseSecond.error as StripeError,
        })

        if (!retryResponseSecond.data.should_retry) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseSecond.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        dispatch(
          setPaymentClientSecretAction(
            createSubscriptionResponse.data.purchase.client_secret,
          ),
        )
        dispatch(
          setSubscriptionIdAction(
            createSubscriptionResponse.data.purchase.subscription_id,
          ),
        )

        const cardPaymentResponseThird = await stripe.confirmCardPayment(
          retryResponseSecond.data.subscription.client_secret,
          {
            payment_method: createPaymentResponse.paymentMethod.id,
            save_payment_method: true,
            return_url: getRedirectUrl(paymentPageId),
          },
        )

        if (
          cardPaymentResponseThird?.paymentIntent &&
          !cardPaymentResponseThird?.error
        ) {
          const predictedLtvResponse = await userApi.getUserPredictedLtv(uuid)

          logSuccessfulPayment({
            ...commonPurchaseSuccessParams,
            predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
            predictedLtvCurrency: predictedLtvResponse?.data?.currency,
            productId: createSubscriptionResponse.data.purchase.product_id,
            trialPrice:
              cardPaymentResponseThird.paymentIntent.amount / CENTS_IN_DOLLAR,
            trialPeriodDays:
              retryResponseSecond.data.subscription.trial_period_days,
            subscriptionId:
              retryResponseSecond.data.subscription.subscription_id,
            discountApplied:
              retryResponseSecond.data.subscription.discount_applied,
            paymentMethod,
            paymentSystem: PaymentSystem.STRIPE,
            isUpsell: false,
            purchaseType: SubscriptionListType.PURCHASE,
          })

          dispatch(
            sendUserConfigAction({
              payment_currency: currency,
              payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
              discount_applied:
                !!retryResponseSecond.data.subscription.discount_applied,
              subscription_price: currentPrice,
              subscription_duration: `${periodQuantity}${periodName}`,
              price_id: priceId,
              trial_price: trialPrice,
              trial_period: trialPeriodDays,
            }),
          )
          dispatch(sendPlanAdditionsAction())
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('success')
          return
        }

        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: cardPaymentResponseThird.error,
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })

        if (!checkIsRetryAllowed(cardPaymentResponseThird)) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseThird.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        const retryResponseThird = await paymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseThird.error as StripeError,
        })

        if (!retryResponseThird.data.should_retry) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseThird.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        dispatch(
          setPaymentClientSecretAction(
            createSubscriptionResponse.data.purchase.client_secret,
          ),
        )
        dispatch(
          setSubscriptionIdAction(
            createSubscriptionResponse.data.purchase.subscription_id,
          ),
        )

        const cardPaymentResponseFourth = await stripe.confirmCardPayment(
          retryResponseThird.data.subscription.client_secret,
          {
            payment_method: createPaymentResponse.paymentMethod.id,
            save_payment_method: true,
            return_url: getRedirectUrl(paymentPageId),
          },
        )

        if (
          cardPaymentResponseFourth?.paymentIntent &&
          !cardPaymentResponseFourth?.error
        ) {
          const predictedLtvResponse = await userApi.getUserPredictedLtv(uuid)

          logSuccessfulPayment({
            ...commonPurchaseSuccessParams,
            predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
            predictedLtvCurrency: predictedLtvResponse?.data?.currency,
            productId: createSubscriptionResponse.data.purchase.product_id,
            trialPrice:
              cardPaymentResponseFourth.paymentIntent.amount / CENTS_IN_DOLLAR,
            trialPeriodDays:
              retryResponseThird.data.subscription.trial_period_days,
            subscriptionId:
              retryResponseThird.data.subscription.subscription_id,
            discountApplied:
              retryResponseThird.data.subscription.discount_applied,
            paymentMethod,
            paymentSystem: PaymentSystem.STRIPE,
            isUpsell: false,
            purchaseType: SubscriptionListType.PURCHASE,
          })

          dispatch(
            sendUserConfigAction({
              payment_currency: currency,
              payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
              discount_applied:
                !!retryResponseThird.data.subscription.discount_applied,
              subscription_price: currentPrice,
              subscription_duration: `${periodQuantity}${periodName}`,
              price_id: priceId,
              trial_price: trialPrice,
              trial_period: trialPeriodDays,
            }),
          )
          dispatch(sendPlanAdditionsAction())
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('success')
          return
        }

        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: cardPaymentResponseFourth.error,
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })

        dispatch(
          setErrorAction(
            getErrorActionPayload(cardPaymentResponseFourth.error),
          ),
        )

        // Needed for reset invoice on BE
        await paymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseFourth.error as StripeError,
        })

        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      const { paymentIntent } = cardPaymentResponseFirst

      if (paymentIntent.status === 'requires_payment_method') {
        eventLogger.logPurchaseFailed({
          ...commonPurchaseFailedParams,
          error: {
            type: 'requires_payment_method',
          },
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })
        dispatch(setErrorAction('Payment failed'))
        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      const threeDSesureURL = paymentIntent.next_action?.redirect_to_url?.url

      if (paymentIntent.status === 'requires_action' && threeDSesureURL) {
        dispatch(set3DSecureIframeUrlAction(threeDSesureURL))
        dispatch(stopFetching(PURCHASE))
        return
      }

      const predictedLtvResponse = await userApi.getUserPredictedLtv(uuid)

      logSuccessfulPayment({
        ...commonPurchaseSuccessParams,
        predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
        predictedLtvCurrency: predictedLtvResponse?.data?.currency,
        productId: createSubscriptionResponse.data.purchase.product_id,
        trialPeriodDays:
          createSubscriptionResponse.data.purchase.trial_period_days,
        subscriptionId:
          createSubscriptionResponse.data.purchase.subscription_id,
        paymentMethod,
        paymentSystem: PaymentSystem.STRIPE,
        isUpsell: false,
        purchaseType: SubscriptionListType.PURCHASE,
      })

      dispatch(
        sendUserConfigAction({
          payment_currency: currency,
          payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
          subscription_price: currentPrice,
          subscription_duration: `${periodQuantity}${periodName}`,
          price_id: priceId,
          trial_price: trialPrice,
          trial_period: trialPeriodDays,
        }),
      )
      dispatch(sendPlanAdditionsAction())
      dispatch(stopFetching(PURCHASE))
      createPaymentResFromDigitalWallet?.complete('success')
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(PURCHASE))
      createPaymentResFromDigitalWallet?.complete('fail')
    }
  }

export const boletoPurchaseAction =
  ({ stripe, boletoData }: { stripe: Stripe; boletoData: IBoletoData }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const planId = selectPlanId(state)
    const priceId = selectSubscriptionLookupKey(state)
    const trialPrice = selectSubscriptionTrialPeriodPrice(state)
    const uuid = selectUUID(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const currency = selectCurrency(state)
    const cohort = selectCurrentVariantCohort(state)
    const selectedSubscription = selectSubscription(state)
    const email = selectEmail(state)
    const paymentMethod = selectPaymentMethod(state)

    if (!boletoData) return

    if (!priceId || !currentPrice) {
      console.error('Error: no subscriptions plan selected')
      return
    }

    const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    dispatch(startFetching(PURCHASE))

    googleAnalyticsLogger.logAddingToCart({
      subscription: selectedSubscription as ISubscription,
      email,
      uuid,
    })
    window.fbq('track', 'AddToCart', {}, { eventID: uuid })
    window.ttq.track('AddToCart')
    window.snaptr &&
      window.snaptr('track', 'ADD_CART', {
        user_email: commonPurchaseStartedParams.isPersonalDataAllowed
          ? email
          : '',
      })
    window.axon &&
      window.axon('track', 'begin_checkout', {
        value: trialPrice || currentPrice,
        currency: currency.toUpperCase(),
        items: getAxonItems([selectedSubscription as ISubscription]),
      })

    eventLogger.logPurchaseStarted({
      ...commonPurchaseStartedParams,
      paymentMethod,
      paymentSystem: PaymentSystem.STRIPE,
    })

    try {
      const createSubscriptionResponse = await paymentApi.createSubscription({
        cohort,
        uuid,
        planId,
        trialPeriodDays,
        cpf: boletoData.taxId,
      })

      if (
        !createSubscriptionResponse.success ||
        !createSubscriptionResponse.data
      ) {
        switch (createSubscriptionResponse.status) {
          case 409:
            dispatch(setErrorAction(i18n.t('payment.haveSubscription')))
            break
          default:
            dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
        }

        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: { type: createSubscriptionResponse.data?.error },
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })

        dispatch(stopFetching(PURCHASE))
        return
      }

      const boletoPaymentResponse = await stripe.confirmBoletoPayment(
        createSubscriptionResponse.data.purchase.client_secret,
        {
          payment_method: {
            boleto: {
              tax_id: boletoData.taxId,
            },
            billing_details: {
              name: boletoData.name,
              email,
              address: {
                line1: boletoData.address,
                city: boletoData.city,
                state: boletoData.state,
                postal_code: boletoData.postalCode,
                country: 'BR',
              },
            },
          },
        },
      )

      if (boletoPaymentResponse?.error) {
        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: boletoPaymentResponse.error,
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })
        dispatch(
          setErrorAction(getErrorActionPayload(boletoPaymentResponse.error)),
        )
        dispatch(stopFetching(PURCHASE))
        return
      }

      if (boletoPaymentResponse?.paymentIntent.status === 'requires_action') {
        dispatch(stopFetching(PURCHASE))
        dispatch(setIsBoletoVoucherClosed(true))
        return
      }

      dispatch(stopFetching(PURCHASE))
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(PURCHASE))
    }
  }

export const sendEventsForUpiPixPayment =
  (): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const priceId = selectSubscriptionLookupKey(state)
    const currency = selectCurrency(state)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
    const pixPurchaseData = selectPixPurchaseData(state)
    const upiPurchaseData = selectUpiPurchaseData(state)

    const purchaseData = pixPurchaseData || upiPurchaseData
    const paymentMethod = pixPurchaseData
      ? PaymentMethod.PIX
      : PaymentMethod.UPI

    if (purchaseData) {
      logSuccessfulPayment({
        ...commonPurchaseSuccessParams,
        screenName: ScreenName.ONBOARDING,
        productId: purchaseData.product_id,
        trialPeriodDays: purchaseData.trial_period_days,
        subscriptionId: purchaseData.subscription_id,
        paymentMethod,
        paymentSystem: PaymentSystem.STRIPE,
        price: purchaseData.subscription_price,
        isUpsell: true,
        purchaseType: SubscriptionListType.UPSELL,
      })
      await dispatch(
        sendUserConfigAction({
          payment_currency: currency,
          payment_method: paymentMethod,
          subscription_price: purchaseData.subscription_price,
          subscription_duration: purchaseData.subscription_duration,
          price_id: priceId,
          trial_price: purchaseData.trial_price,
          trial_period: purchaseData.trial_period_days,
        }),
      )
      if (pixPurchaseData) {
        dispatch(setIsPixVoucherClosed(false))
      }
      if (upiPurchaseData) {
        dispatch(setWasUpiPaymentInitiated(false))
      }
    }
  }

export const sendEventsForAppleGooglePay =
  ({ paymentMethod }: { paymentMethod: PaymentMethod }): any =>
  async (_, getState: () => IAppState): Promise<void> => {
    const state = getState()
    const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)

    eventLogger.logPurchaseStarted({
      ...commonPurchaseStartedParams,
      paymentMethod,
      paymentSystem: PaymentSystem.STRIPE,
    })
  }

export const pixPurchaseAction =
  ({ stripe, name }: { stripe: Stripe; name: string }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const planId = selectPlanId(state)
    const priceId = selectSubscriptionLookupKey(state)
    const trialPrice = selectSubscriptionTrialPeriodPrice(state)
    const uuid = selectUUID(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const currency = selectCurrency(state)
    const cohort = selectCurrentVariantCohort(state)
    const selectedSubscription = selectSubscription(state)
    const email = selectEmail(state)
    const paymentMethod = selectPaymentMethod(state)
    const cpf = selectCpf(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)

    if (!priceId || !currentPrice) {
      console.error('Error: no subscriptions plan selected')
      return
    }

    const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)

    dispatch(startFetching(PURCHASE))

    googleAnalyticsLogger.logAddingToCart({
      subscription: selectedSubscription as ISubscription,
      email,
      uuid,
    })
    window.fbq('track', 'AddToCart', {}, { eventID: uuid })
    window.ttq.track('AddToCart')
    window.snaptr &&
      window.snaptr('track', 'ADD_CART', {
        user_email: commonPurchaseStartedParams.isPersonalDataAllowed
          ? email
          : '',
      })
    window.axon &&
      window.axon('track', 'begin_checkout', {
        value: trialPrice || currentPrice,
        currency: currency.toUpperCase(),
        items: getAxonItems([selectedSubscription as ISubscription]),
      })

    eventLogger.logPurchaseStarted({
      ...commonPurchaseStartedParams,
      paymentMethod,
      paymentSystem: PaymentSystem.STRIPE,
    })

    try {
      const createSubscriptionResponse = await paymentApi.createSubscription({
        cohort,
        uuid,
        planId,
        trialPeriodDays,
        cpf,
      })

      if (
        !createSubscriptionResponse.success ||
        !createSubscriptionResponse.data
      ) {
        switch (createSubscriptionResponse.status) {
          case 409:
            dispatch(setErrorAction(i18n.t('payment.haveSubscription')))
            break
          default:
            dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
        }

        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: { type: createSubscriptionResponse.data?.error },
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })

        dispatch(stopFetching(PURCHASE))
        return
      }

      dispatch(
        sendUserConfigAction({
          pix_purchase_data: {
            product_id: createSubscriptionResponse.data.purchase.product_id,
            subscription_id:
              createSubscriptionResponse.data.purchase.subscription_id,
            trial_period_days:
              createSubscriptionResponse.data.purchase.trial_period_days,
            subscription_price: currentPrice,
            trial_price: trialPrice,
            subscription_duration: `${periodQuantity}${periodName}`,
          },
        }),
      )

      const pixPaymentResponse = await stripe.confirmPixPayment(
        createSubscriptionResponse.data.purchase.client_secret,
        {
          payment_method: {
            billing_details: {
              name,
              email,
              tax_id: cpf,
            },
          } as any,
        },
      )

      if (pixPaymentResponse?.error) {
        const { type, message, code } = pixPaymentResponse.error
        const errorText = i18n.t('commonComponents.commonError')
        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: { type, message, code },
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })
        dispatch(setErrorAction(errorText))
        dispatch(
          sendUserConfigAction({
            pix_purchase_data: null,
          }),
        )
        dispatch(stopFetching(PURCHASE))
        return
      }

      if (pixPaymentResponse?.paymentIntent?.status === 'requires_action') {
        dispatch(stopFetching(PURCHASE))
        dispatch(setIsPixVoucherClosed(true))
        return
      }

      if (pixPaymentResponse?.paymentIntent?.last_payment_error) {
        const message = pixPaymentResponse.paymentIntent.last_payment_error
        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: message,
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })
        dispatch(setErrorAction(getErrorActionPayload(message)))
        dispatch(
          sendUserConfigAction({
            pix_purchase_data: null,
          }),
        )
        dispatch(stopFetching(PURCHASE))
        return
      }

      if (pixPaymentResponse?.paymentIntent?.status === 'succeeded') {
        logSuccessfulPayment({
          ...commonPurchaseSuccessParams,
          productId: createSubscriptionResponse.data.purchase.product_id,
          trialPeriodDays:
            createSubscriptionResponse.data.purchase.trial_period_days,
          subscriptionId:
            createSubscriptionResponse.data.purchase.subscription_id,
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
          isUpsell: false,
          purchaseType: SubscriptionListType.PURCHASE,
        })
        dispatch(
          sendUserConfigAction({
            payment_currency: currency,
            payment_method: PaymentMethod.PIX,
            subscription_price: currentPrice,
            subscription_duration: `${periodQuantity}${periodName}`,
            price_id: priceId,
            trial_price: trialPrice,
            trial_period: trialPeriodDays,
            pix_purchase_data: null,
          }),
        )
        dispatch(setIsPixVoucherClosed(false))
        dispatch(stopFetching(PURCHASE))
      }
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(PURCHASE))
    }
  }

export const upiPurchaseAction =
  ({
    stripe,
    upiId,
    name,
    address,
    postalCode,
  }: {
    stripe: Stripe
    upiId: string
    name: string
    address: string
    postalCode: string
  }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const planId = selectPlanId(state)
    const priceId = selectSubscriptionLookupKey(state)
    const trialPrice = selectSubscriptionTrialPeriodPrice(state)
    const uuid = selectUUID(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const currency = selectCurrency(state)
    const cohort = selectCurrentVariantCohort(state)
    const selectedSubscription = selectSubscription(state)
    const email = selectEmail(state)
    const paymentMethod = selectPaymentMethod(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)

    if (!priceId || !currentPrice) {
      console.error('Error: no subscriptions plan selected')
      return
    }

    const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)

    dispatch(startFetching(PURCHASE))

    googleAnalyticsLogger.logAddingToCart({
      subscription: selectedSubscription as ISubscription,
      email,
      uuid,
    })
    window.fbq('track', 'AddToCart', {}, { eventID: uuid })
    window.ttq.track('AddToCart')
    window.snaptr &&
      window.snaptr('track', 'ADD_CART', {
        user_email: commonPurchaseStartedParams.isPersonalDataAllowed
          ? email
          : '',
      })
    window.axon &&
      window.axon('track', 'begin_checkout', {
        value: trialPrice || currentPrice,
        currency: currency.toUpperCase(),
        items: getAxonItems([selectedSubscription as ISubscription]),
      })

    eventLogger.logPurchaseStarted({
      ...commonPurchaseStartedParams,
      paymentMethod,
      paymentSystem: PaymentSystem.STRIPE,
    })

    try {
      const createSubscriptionResponse = await paymentApi.createSubscription({
        cohort,
        uuid,
        planId,
        trialPeriodDays,
        upiVpa: upiId,
      })

      if (
        !createSubscriptionResponse.success ||
        !createSubscriptionResponse.data
      ) {
        switch (createSubscriptionResponse.status) {
          case 409:
            dispatch(setErrorAction(i18n.t('payment.haveSubscription')))
            break
          default:
            dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
        }

        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: { type: createSubscriptionResponse.data?.error },
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })

        dispatch(stopFetching(PURCHASE))
        return
      }

      dispatch(
        sendUserConfigAction({
          upi_purchase_data: {
            product_id: createSubscriptionResponse.data.purchase.product_id,
            subscription_id:
              createSubscriptionResponse.data.purchase.subscription_id,
            trial_period_days:
              createSubscriptionResponse.data.purchase.trial_period_days,
            subscription_price: currentPrice,
            trial_price: trialPrice,
            subscription_duration: `${periodQuantity}${periodName}`,
          },
        }),
      )

      const upiPaymentResponse = await (stripe as any).confirmUpiPayment(
        createSubscriptionResponse.data.purchase.client_secret,
        {
          payment_method: {
            upi: {},
            billing_details: {
              name,
              email,
              address: {
                country: 'IN',
                line1: address,
                postal_code: postalCode,
              },
            },
          },
          return_url: window.location.href,
        },
      )

      if (upiPaymentResponse?.error) {
        const { type, message, code } = upiPaymentResponse.error
        const errorText = i18n.t('commonComponents.commonError')
        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: { type, message, code },
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })
        dispatch(setErrorAction(errorText))
        dispatch(
          sendUserConfigAction({
            upi_purchase_data: null,
          }),
        )
        dispatch(stopFetching(PURCHASE))
        return
      }

      if (upiPaymentResponse?.paymentIntent?.status === 'requires_action') {
        dispatch(stopFetching(PURCHASE))
        dispatch(setWasUpiPaymentInitiated(true))
        return
      }

      if (upiPaymentResponse?.paymentIntent?.last_payment_error) {
        const message = upiPaymentResponse.paymentIntent.last_payment_error
        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: message,
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
        })
        dispatch(setErrorAction('Payment failed'))
        dispatch(
          sendUserConfigAction({
            upi_purchase_data: null,
          }),
        )
        dispatch(stopFetching(PURCHASE))
        return
      }

      if (upiPaymentResponse?.paymentIntent?.status === 'succeeded') {
        logSuccessfulPayment({
          ...commonPurchaseSuccessParams,
          productId: createSubscriptionResponse.data.purchase.product_id,
          trialPeriodDays:
            createSubscriptionResponse.data.purchase.trial_period_days,
          subscriptionId:
            createSubscriptionResponse.data.purchase.subscription_id,
          paymentMethod,
          paymentSystem: PaymentSystem.STRIPE,
          isUpsell: false,
          purchaseType: SubscriptionListType.PURCHASE,
        })
        dispatch(
          sendUserConfigAction({
            payment_currency: currency,
            payment_method: PaymentMethod.UPI,
            subscription_price: currentPrice,
            subscription_duration: `${periodQuantity}${periodName}`,
            price_id: priceId,
            trial_price: trialPrice,
            trial_period: trialPeriodDays,
            upi_purchase_data: null,
          }),
        )
        dispatch(setWasUpiPaymentInitiated(false))
        dispatch(stopFetching(PURCHASE))
      }
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(PURCHASE))
    }
  }

export const check3DSecure =
  (stripe: Stripe): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)
    const priceId = selectSubscriptionLookupKey(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const trialPeriodPrice = selectSubscriptionTrialPeriodPrice(state)
    const clientSecret = selectPaymentClientSecret(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const subscriptionId = selectCreatedSubscriptionId(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)
    const currency = selectCurrency(state)

    if (!priceId || !currentPrice) {
      console.error('Error: no subscriptions plan selected')
      return
    }

    if (!clientSecret) {
      console.error('Error: client secret is needed')
      return
    }

    dispatch(startFetching(CHECK_3D_SECURE))

    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    const response = await stripe.retrievePaymentIntent(clientSecret)

    if (response.paymentIntent?.status === 'succeeded') {
      const predictedLtvResponse = await userApi.getUserPredictedLtv(uuid)

      logSuccessfulPayment({
        ...commonPurchaseSuccessParams,
        predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
        predictedLtvCurrency: predictedLtvResponse?.data?.currency,
        trialPeriodDays,
        subscriptionId,
        paymentSystem: PaymentSystem.STRIPE,
        isUpsell: false,
        purchaseType: SubscriptionListType.PURCHASE,
      })
      dispatch(reset3DSecureIframeUrlAction())
      dispatch(
        sendUserConfigAction({
          payment_currency: currency,
          payment_method: PaymentMethod.CREDIT_CARD,
          subscription_price: currentPrice,
          subscription_duration: `${periodQuantity}${periodName}`,
          price_id: priceId,
          trial_price: trialPeriodPrice,
          trial_period: trialPeriodDays,
        }),
      )
      return
    }

    if (response.paymentIntent?.status === 'requires_payment_method') {
      eventLogger.logPurchaseFailed({
        ...commonPurchaseFailedParams,
        error: {
          type: 'requires_payment_method',
        },
        paymentSystem: PaymentSystem.STRIPE,
      })
      dispatch(reset3DSecureIframeUrlAction())
      dispatch(setErrorAction('Payment failed'))
      dispatch(stopFetching(CHECK_3D_SECURE))
      return
    }

    if (response.error) {
      const {
        error: { type, message },
      } = response

      eventLogger.logPurchaseFailed({
        ...commonPurchaseFailedParams,
        error: response.error,
        paymentSystem: PaymentSystem.STRIPE,
      })
      dispatch(reset3DSecureIframeUrlAction())
      dispatch(setErrorAction(message || type))
      dispatch(stopFetching(CHECK_3D_SECURE))
      return
    }

    dispatch(reset3DSecureIframeUrlAction())
    dispatch(setErrorAction('Error: unhandled checking 3D Secure error'))
    dispatch(stopFetching(CHECK_3D_SECURE))
  }

export const purchaseUpgrade = async (
  dispatch: TAppDispatchThunk<any>,
  getState: () => IAppState,
): Promise<void> => {
  const state = getState()
  const cohort = selectCurrentVariantCohort(state)
  const uuid = selectUUID(state)
  const planId = selectPlanId(state)
  const currency = selectCurrency(state)
  const fullPrice = selectSubscriptionFullPrice(state)
  const amountToPay = selectUpgradeAmountToPay(state)
  const priceId = selectSubscriptionLookupKey(state)
  const paymentMethod = selectUserPaymentMethod(state)
  const paymentSystem = selectUserPaymentSystem(state)
  const isUpgradePassed = selectIsUpgradePassed(state)
  const periodQuantity = selectSubscriptionPeriodQuantity(state)
  const periodName = selectSubscriptionPeriodName(state)
  const trialPrice = selectSubscriptionTrialPeriodPrice(state)
  const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)

  dispatch(startFetching(PURCHASE))

  const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
  const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
  const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

  if (isUpgradePassed) return

  eventLogger.logPurchaseStarted({
    ...commonPurchaseStartedParams,
    paymentMethod,
    priceDetails: {
      currency,
      price: fullPrice,
    },
    paymentSystem,
    isUpgraded: true,
    amountToPay,
    productId: priceId,
  })

  try {
    const createSubscriptionResponse =
      paymentSystem === PaymentSystem.PRIMER
        ? await paymentApi.upgradePrimerSubscription({
            uuid,
            planId,
            cohort,
          })
        : await paymentApi.upgradeSubscription({
            uuid,
            planId,
            cohort,
          })

    if (
      !createSubscriptionResponse.success ||
      !createSubscriptionResponse.data
    ) {
      let errorText: string

      switch (createSubscriptionResponse.data?.decline_code) {
        case StripeSoftDeclineReason.INSUFFICIENT_FUNDS:
          errorText = i18n.t('payment.insufficientFunds')
          dispatch(setErrorAction(errorText))
          break
        case StripeDeclineReason.STOLEN_CARD:
        case StripeDeclineReason.LOST_CARD:
        case StripeDeclineReason.STRIPE_GENERIC_DECLINE_ERROR:
          errorText = i18n.t('payment.genericDecline')
          dispatch(setErrorAction(errorText))
          break
        default:
          errorText = i18n.t('commonComponents.commonError')
          dispatch(setErrorAction(errorText))
      }

      eventLogger.logPurchaseFailed({
        ...commonPurchaseFailedParams,
        error: {
          type: createSubscriptionResponse.data?.error,
          code: createSubscriptionResponse.data?.decline_code,
          description: errorText,
        },
        paymentMethod,
        isUpgraded: true,
        amountToPay,
        productId: priceId,
        paymentSystem,
      })

      dispatch(stopFetching(PURCHASE))
      return
    }

    eventLogger.logPurchaseCompleted({
      ...commonPurchaseSuccessParams,
      priceDetails: {
        price: fullPrice,
        periodQuantity,
        periodName,
        isTrial: !!trialPrice,
        trialPrice,
        trialPeriodDays,
        currency,
      },
      paymentMethod,
      utmSource: Source.UPGRADE_OFFER,
      isUpgraded: true,
      amountToPay,
      productId: createSubscriptionResponse.data.upgrade.product_id,
      paymentSystem,
      isUpsell: false,
      purchaseType: SubscriptionListType.UPGRADE,
    })

    dispatch(sendPlanAdditionsAction())
    dispatch(stopFetching(PURCHASE))
  } catch (error: any) {
    dispatch(setErrorAction(error.toString()))
    dispatch(stopFetching(PURCHASE))
  }
}

export const makeUpsellAction =
  (): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)
    const planId = selectPlanId(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const paymentMethod = selectUserPaymentMethod(state)
    const productId = selectProductId(state)
    const paymentSystem = selectUserPaymentSystem(state)
    const screenName = selectScreenName(state)
    const processorName = selectProcessorName(state)
    const pixPurchaseData = selectPixPurchaseData(state)

    dispatch(startFetching(MAKE_UPSELL))

    const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    eventLogger.logPurchaseStarted({
      ...commonPurchaseStartedParams,
      paymentMethod: pixPurchaseData ? PaymentMethod.PIX : paymentMethod,
      paymentSystem,
      productKey: productId,
      ...(paymentSystem === PaymentSystem.PRIMER && { processorName }),
    })

    const dto = {
      uuid,
      productId,
      planId,
    }

    const upsellResponse =
      paymentSystem === PaymentSystem.PRIMER
        ? await paymentApi.makePrimerUpsell(dto)
        : await paymentApi.makeUpsell(dto)

    if (upsellResponse.success && upsellResponse.data) {
      logSuccessfulPayment({
        ...commonPurchaseSuccessParams,
        trialPeriodDays,
        subscriptionId: upsellResponse.data.subscription.subscription_id,
        paymentMethod: pixPurchaseData ? PaymentMethod.PIX : paymentMethod,
        paymentSystem,
        productId: upsellResponse.data.subscription.product_id,
        productKey: productId,
        screenName,
        isUpsell: true,
        purchaseType: SubscriptionListType.UPSELL,
        ...(paymentSystem === PaymentSystem.PRIMER && { processorName }),
      })

      dispatch(sendPlanAdditionsAction())
      dispatch(stopFetching(MAKE_UPSELL))
      return
    }

    let errorText: string

    switch (upsellResponse.data?.decline_code) {
      case StripeSoftDeclineReason.INSUFFICIENT_FUNDS:
        errorText = i18n.t('payment.insufficientFunds')
        dispatch(setErrorAction(errorText))
        break
      case StripeSoftDeclineReason.CARD_VELOCITY_EXCEEDED:
        errorText = i18n.t('payment.velocityExceeded')
        dispatch(setErrorAction(errorText))
        break
      case StripeDeclineReason.STOLEN_CARD:
      case StripeDeclineReason.LOST_CARD:
      case StripeDeclineReason.STRIPE_GENERIC_DECLINE_ERROR:
        errorText = i18n.t('payment.genericDecline')
        dispatch(setErrorAction(errorText))
        break
      default:
        errorText = i18n.t('commonComponents.commonError')
        dispatch(setErrorAction(errorText))
    }

    eventLogger.logPurchaseFailed({
      ...commonPurchaseFailedParams,
      paymentMethod: pixPurchaseData ? PaymentMethod.PIX : paymentMethod,
      paymentSystem,
      productKey: productId,
      ...(paymentSystem === PaymentSystem.PRIMER && { processorName }),
      error: {
        type: upsellResponse.data?.error,
        code: upsellResponse.data?.decline_code,
        description: errorText,
      },
    })
    dispatch(stopFetching(MAKE_UPSELL))
  }
